import React, { ImgHTMLAttributes, SourceHTMLAttributes } from 'react';
import { ImageCmsFile } from '@apps/Shared/Types/ImageFile';

declare const process: { env?: { NODE_ENV?: string } };

const BASE_IMAGE_RENDER_CDN_OPTIONS = 'quality=75,format=webp';
const WIDTHS_SMALL_DEVICE = [384, 480, 576, 640, 704, 768];
const BASE_WIDTHS = [384, 480, 576, 640, 704, 768, 832, 896, 960, 992, 1056, 1200, 1344, 1400, 1488, 1632, 1776, 1920, 2800, 3840];
const SMALL_DEVICE_PORTRAIT_WIDTHS = [320, 375, 414, 828, 1170, 1242, 1284];

const PICTURE_SOURCE_MEDIA_ATTRIBUTE_LARGE_DEVICE = '(min-width: 767px)';
const PICTURE_SOURCE_MEDIA_ATTRIBUTE_SMALL_DEVICE = '(max-width: 766.99px)';
const PICTURE_SOURCE_MEDIA_ATTRIBUTE_SMALL_DEVICE_PORTRAIT = '(max-aspect-ratio: 1/1)';

const isDevelopmentEnvironment = process.env?.NODE_ENV !== 'production';

type AdditionalAttributes = ImgHTMLAttributes<HTMLImageElement> & Record<string, unknown>;

export type SingleImageProps = {
    url?: string | null;
    altText?: string | null;
    additionalClasses?: string | null;
    additionalAttributes?: AdditionalAttributes;
    image?: ImageCmsFile | null;
    sizes?: string[] | null;
};

export type MultipleImagesProps = {
    altText?: string | null;
    additionalClasses?: string | null;
    imageSmallDevice?: ImageCmsFile | null;
    imageLargeDevice?: ImageCmsFile | null;
    additionalAttributes?: AdditionalAttributes;
    sizes?: string[] | null;
};

const getImageUrl = (image?: ImageCmsFile | null, url?: string | null) => {
    return image?.RelativePath || url || '';
};

const joinClassNames = (additionalClasses?: string | null, className?: string) => {
    return [className, additionalClasses].filter(Boolean).join(' ') || undefined;
};

const hasPngExtension = (baseUrl: string) => {
    const [path] = baseUrl.split(/[?#]/);

    return path.toLowerCase().endsWith('.png');
};

const getSrcSetAttribute = (baseUrl: string, widths: number[]) => {
    if (isDevelopmentEnvironment && hasPngExtension(baseUrl)) {
        return '';
    }

    return widths
        .map((width) =>
            isDevelopmentEnvironment
                ? `${baseUrl}/width${width}w ${width}w`
                : `/cdn-cgi/image/${BASE_IMAGE_RENDER_CDN_OPTIONS},width=${width}${baseUrl} ${width}w`
        )
        .join(', ');
};

const getPortraitSrcSetAttribute = (baseUrl: string) => {
    if (isDevelopmentEnvironment) {
        return `${baseUrl}/Portrait320x720 320w, ${baseUrl}/Portrait375x833 375w, ${baseUrl}/Portrait414x920 414w, ${baseUrl}/Portrait828x1845 828w, ${baseUrl}/Portrait1170x2606 1170w, ${baseUrl}/Portrait1242x2787 1242w, ${baseUrl}/Portrait1284x2856 1284w`;
    }

    return getSrcSetAttribute(baseUrl, SMALL_DEVICE_PORTRAIT_WIDTHS);
};

const mapImageAttributes = (
    altText?: string | null,
    additionalClasses?: string | null,
    additionalAttributes?: AdditionalAttributes,
    lazy = true,
    src?: string,
    srcSet?: string
) => {
    const attributes: ImgHTMLAttributes<HTMLImageElement> = {
        ...additionalAttributes,
        className: joinClassNames(additionalClasses, additionalAttributes?.className),
    };

    if (src) {
        attributes.src = src;
    }

    if (srcSet) {
        attributes.srcSet = srcSet;
    }

    if (lazy) {
        attributes.loading = 'lazy';
    }

    if (altText !== null && altText !== undefined) {
        attributes.alt = altText;
    }

    return attributes;
};

const mapSourceAttributes = (media: string, sizes: string[], srcSet: string): SourceHTMLAttributes<HTMLSourceElement> => {
    return {
        media,
        sizes: sizes.join(', '),
        srcSet,
    };
};

export const renderSingleImage = (singleImage: SingleImageProps, lazy = true) => {
    if (!singleImage || (!singleImage.url && !singleImage.image)) {
        return null;
    }

    const baseUrl = getImageUrl(singleImage.image, singleImage.url);
    const srcSet = getSrcSetAttribute(baseUrl, BASE_WIDTHS);

    if (singleImage.image && !singleImage.sizes) {
        throw new Error(`Image "${baseUrl}" is missing sizes!`);
    }

    return (
        <img
            {...mapImageAttributes(
                singleImage.altText,
                singleImage.additionalClasses,
                singleImage.additionalAttributes,
                lazy,
                baseUrl,
                srcSet
            )}
            sizes={singleImage.sizes?.join(', ')}
        />
    );
};

export const renderMultipleImages = (
    multipleImage?: MultipleImagesProps | null,
    lazy = true,
    renderImageSmallDevicePortrait = false
) => {
    if (!multipleImage?.imageLargeDevice) {
        return null;
    }

    const imageSmallDevice =
        !multipleImage.imageSmallDevice && !renderImageSmallDevicePortrait
            ? multipleImage.imageLargeDevice
            : multipleImage.imageSmallDevice;

    const baseUrlSmallDevice = getImageUrl(imageSmallDevice);
    const baseUrlLargeDevice = getImageUrl(multipleImage.imageLargeDevice);

    if (!multipleImage.sizes) {
        throw new Error(`Image "${baseUrlLargeDevice}" is missing sizes!`);
    }

    const sources: React.ReactNode[] = [];

    if (renderImageSmallDevicePortrait && !multipleImage.imageSmallDevice) {
        sources.push(
            <source
                key="small-device-portrait"
                {...mapSourceAttributes(
                    PICTURE_SOURCE_MEDIA_ATTRIBUTE_SMALL_DEVICE_PORTRAIT,
                    multipleImage.sizes,
                    getPortraitSrcSetAttribute(baseUrlLargeDevice) || baseUrlLargeDevice
                )}
            />
        );
    }

    sources.push(
        <source
            key="small-device"
            {...mapSourceAttributes(
                PICTURE_SOURCE_MEDIA_ATTRIBUTE_SMALL_DEVICE,
                multipleImage.sizes,
                getSrcSetAttribute(baseUrlSmallDevice, WIDTHS_SMALL_DEVICE) || baseUrlSmallDevice
            )}
        />
    );

    sources.push(
        <source
            key="large-device"
            {...mapSourceAttributes(
                PICTURE_SOURCE_MEDIA_ATTRIBUTE_LARGE_DEVICE,
                multipleImage.sizes,
                getSrcSetAttribute(baseUrlLargeDevice, BASE_WIDTHS) || baseUrlLargeDevice
            )}
        />
    );

    return (
        <picture>
            {sources}
            <img
                {...mapImageAttributes(
                    multipleImage.altText,
                    multipleImage.additionalClasses,
                    multipleImage.additionalAttributes,
                    lazy,
                    baseUrlLargeDevice
                )}
            />
        </picture>
    );
};

export const imageRenderConstants = {
    baseWidths: BASE_WIDTHS,
    pictureSourceMediaAttributeLargeDevice: PICTURE_SOURCE_MEDIA_ATTRIBUTE_LARGE_DEVICE,
    pictureSourceMediaAttributeSmallDevice: PICTURE_SOURCE_MEDIA_ATTRIBUTE_SMALL_DEVICE,
    pictureSourceMediaAttributeSmallDevicePortrait: PICTURE_SOURCE_MEDIA_ATTRIBUTE_SMALL_DEVICE_PORTRAIT,
    smallDevicePortraitWidths: SMALL_DEVICE_PORTRAIT_WIDTHS,
    widthsSmallDevice: WIDTHS_SMALL_DEVICE,
};

export const imageRenderUtils = {
    getPortraitSrcSetAttribute,
    getSrcSetAttribute,
};
